import React, { useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import EzTable from "../../../../common/EzTable";
import tableColumnsConfig from "./tableColumns.json";
import styles from "../style.module.css";

const AffiliatePerformanceTable = ({ 
  affiliateMetrics, 
  isLoading, 
  paginationInfo, 
  onPageChange,
  affiliateReservations,
  isLoadingReservations,
  onFetchReservations,
}) => {
  const [expandedRows, setExpandedRows] = useState(null);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value);
  };

  const handleRowToggle = (e) => {
    const newExpandedRows = e.data;
    setExpandedRows(newExpandedRows);

    if (newExpandedRows) {
      Object.keys(newExpandedRows).forEach((affiliateId) => {
        if (newExpandedRows[affiliateId] && !affiliateReservations[affiliateId]) {
          onFetchReservations({ affiliateId: parseInt(affiliateId), pageNumber: 1, pageSize: 5 });
        }
      });
    }
  };

  const rowExpansionTemplate = (data) => {
    const affiliateData = affiliateReservations[data.affiliateId];
    const reservations = affiliateData?.reservations || [];
    const reservationPaginationInfo = affiliateData?.paginationInfo;
    const isLoadingThisAffiliate = isLoadingReservations[data.affiliateId] || false;

    if (isLoadingThisAffiliate && reservations.length === 0) {
      return (
        <Box className={styles.emptyReservations}>
          <Typography variant="body2" color="textSecondary">
            Loading reservations...
          </Typography>
        </Box>
      );
    }

    if (reservations.length === 0) {
      return (
        <Box className={styles.emptyReservations}>
          <Typography variant="body2" color="textSecondary">
            No reservations found for this affiliate.
          </Typography>
        </Box>
      );
    }

    const transformedRows = reservations.map((item) => ({
      id: item.id,
      ref: item.ref || "-",
      renterName: `${item.renterFirstName || ""} ${item.renterLastName || ""}`.trim() || "-",
      totalAmount: item.totalAmount ? formatCurrency(item.totalAmount) : "-",
      commissionAmount: item.commissionAmount ? formatCurrency(item.commissionAmount) : "-",
      pickupDate: item.pickupDate || "-",
      pickupTime: item.pickupTime || "-",
      dropoffDate: item.dropoffDate || "-",
      dropoffTime: item.dropoffTime || "-",
      reservationStatus: item.reservationStatus || "-",
      locationName: item.locationName || "-",
      networkName: item.networkName || "-",
      createdTime: item.createdTime || "-",
    }));

    const columns = tableColumnsConfig;

    const tableProps = {
      columns,
      value: transformedRows,
      emptyMessage: "No reservations available",
      areFiltersVisible: false,
      showMoreActions: false,
      showViewButton: false,
      showCopyButton: false,
      showDeleteButton: false,
      showEditButton: false,
      loading: isLoadingThisAffiliate,
      paginationInfo: {
        pageSize: reservationPaginationInfo?.size || 5,
        pageNumber: reservationPaginationInfo?.number ? reservationPaginationInfo.number - 1 : 0,
        totalPages: reservationPaginationInfo?.totalPages || 1,
        totalRecords: reservationPaginationInfo?.totalElements || transformedRows.length,
      },
      loadLazyData: (params) => {
        // Convert 0-based page index to 1-based pageNumber
        const pageNumber = (params.pageNumber !== undefined ? params.pageNumber : params.page || 0) + 1;
        const pageSize = params.pageSize || params.rows || 5;
        
        onFetchReservations({ 
          affiliateId: data.affiliateId, 
          pageNumber, 
          pageSize 
        });
      },
      setDeleteConfirmationPopupVisibility: () => {},
    };

    return (
      <Box className={styles.reservationsContainer}>
        <Typography className={styles.reservationsTitle}>
          Reservations for {data.affiliateName}
        </Typography>
        <Box sx={{ overflowX: "auto", width: "100%" }}>
          <EzTable {...tableProps} />
        </Box>
      </Box>
    );
  };

  const transformedMetrics = (affiliateMetrics || []).map((item) => ({
    id: item.affiliateId,
    affiliateId: item.affiliateId,
    affiliateName: item.affiliateName || "-",
    reservationCount: item.reservationCount || 0,
    totalRevenue: formatCurrency(item.totalRevenue),
  }));

  const performanceColumns = [
    {
      colLabel: "Affiliate Name",
      dataKey: "affiliateName",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Reservation Count",
      dataKey: "reservationCount",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
    {
      colLabel: "Total Revenue",
      dataKey: "totalRevenue",
      extraProps: {
        isSortable: true,
        isDateField: false,
        shouldFilter: false,
        hidden: false,
        showFilterMenu: false,
      },
    },
  ];

  const tableProps = {
    columns: performanceColumns,
    value: transformedMetrics,
    emptyMessage: "No affiliate metrics found",
    areFiltersVisible: false,
    showMoreActions: false,
    showViewButton: false,
    showCopyButton: false,
    showDeleteButton: false,
    showEditButton: false,
    loading: isLoading,
    paginationInfo: {
      pageSize: paginationInfo?.size || 10,
      pageNumber: paginationInfo?.number != null ? paginationInfo.number - 1 : 0,
      totalPages: paginationInfo?.totalPages || 1,
      totalRecords: paginationInfo?.totalElements || transformedMetrics.length,
    },
    loadLazyData: (params) => {
      if (onPageChange) {
        onPageChange({
          pageNumber: params.pageNumber,
          pageSize: params.pageSize || params.rows,
        });
      }
    },
    setDeleteConfirmationPopupVisibility: () => {},
    expandedRows: expandedRows,
    onRowToggle: handleRowToggle,
    rowExpansionTemplate: rowExpansionTemplate,
    dataKey: "affiliateId",
  };

  return (
    <Paper className={styles.tableCard} elevation={0}>
      <Typography className={styles.tableTitle}>
        Affiliate Performance Details
      </Typography>
      <EzTable {...tableProps} />
    </Paper>
  );
};

export default AffiliatePerformanceTable;
